import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Send, ArrowLeft, MailPlus, Loader2, X, ImageIcon, Clock, MoreVertical, Check, CheckCheck, Reply, Trash2, Copy, RotateCcw, ChevronDown, WifiOff, Ban } from "lucide-react";
import { toast } from "sonner";
import { AuraRing } from "@/components/brand/AuraRing";
import { VerifiedBadge } from "@/components/brand/VerifiedBadge";
import { StoryViewer } from "@/components/app/Stories";
import { useAuth } from "@/context/AuthContext";
import { usePoll } from "@/hooks/usePoll";
import { useChatSocket } from "@/hooks/useChatSocket";
import { api, mediaUrl, timeAgo, userColor } from "@/lib/api";
import { cn } from "@/lib/utils";

const URL_RE = /(https?:\/\/[^\s]+)/g;
const QUICK = ["❤️", "😂", "👍", "🔥", "😮", "😢", "🙏"];

function Linkify({ text }) {
  if (!text) return null;
  return text.split(URL_RE).map((part, i) =>
    URL_RE.test(part)
      ? <a key={`${i}-${part}`} href={part} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="underline decoration-white/50 hover:opacity-80">{part}</a>
      : <React.Fragment key={`${i}-${part.length}`}>{part}</React.Fragment>
  );
}

function reactionsFromRaw(raw, myId) {
  return Object.entries(raw || {}).filter(([, u]) => u.length).map(([emoji, u]) => ({ emoji, count: u.length, me: u.includes(myId) }));
}

function presenceText(p) {
  if (!p) return "";
  if (p.online) return "Active now";
  if (!p.last_seen) return "Offline";
  return `Active ${timeAgo(p.last_seen)}`;
}

function StatusTicks({ status }) {
  if (status === "sending") return <Loader2 className="h-3 w-3 animate-spin text-white/50" data-testid="status-sending" />;
  if (status === "failed") return <span className="text-[10px] font-bold text-aura-magenta" data-testid="status-failed">Failed</span>;
  if (status === "seen") return <CheckCheck className="h-3.5 w-3.5 text-aura-cyan" data-testid="status-seen" />;
  if (status === "delivered") return <CheckCheck className="h-3.5 w-3.5 text-white/55" data-testid="status-delivered" />;
  return <Check className="h-3.5 w-3.5 text-white/55" data-testid="status-sent" />;
}

export default function Messages() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const myId = user?.user_id;
  const [params] = useSearchParams();
  const [pane, setPane] = useState("chats");
  const [convos, setConvos] = useState(null);
  const [friends, setFriends] = useState(null);
  const [active, setActive] = useState(null);
  const activeRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [text, setText] = useState("");
  const [reply, setReply] = useState(null);
  const [otherTyping, setOtherTyping] = useState(false);
  const typingTimer = useRef(null);
  const lastTypingSent = useRef(0);
  const [uploading, setUploading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [listQ, setListQ] = useState("");
  const [convSearch, setConvSearch] = useState(false);
  const [convQ, setConvQ] = useState("");
  const [convResults, setConvResults] = useState([]);
  const [headMenu, setHeadMenu] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [presence, setPresence] = useState(null);
  const [online, setOnline] = useState(() => new Set());
  const [atBottom, setAtBottom] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const endRef = useRef(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const atBottomRef = useRef(true);

  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { atBottomRef.current = atBottom; }, [atBottom]);

  const loadConvos = useCallback(async () => { try { setConvos(await api.conversations()); } catch { setConvos([]); } }, []);
  const loadFriends = useCallback(async () => { try { setFriends(await api.friends()); } catch { setFriends([]); } }, []);
  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => { if (pane === "friends" && friends === null) loadFriends(); }, [pane, friends, loadFriends]);

  const scrollToBottom = (smooth = true) => { requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" })); };

  function mergeMsg(prev, m) {
    if (m.client_id) {
      const idx = prev.findIndex((x) => x.id === m.client_id);
      if (idx >= 0) { const c = [...prev]; c[idx] = { ...m }; return c; }
    }
    const i2 = prev.findIndex((x) => x.id === m.id);
    if (i2 >= 0) { const c = [...prev]; c[i2] = { ...c[i2], ...m }; return c; }
    return [...prev, m];
  }

  const onWsEvent = useCallback((ev) => {
    const cur = activeRef.current;
    if (ev.t === "ready") { setOnline(new Set(ev.online || [])); return; }
    if (ev.t === "presence") {
      setOnline((s) => { const n = new Set(s); ev.online ? n.add(ev.user_id) : n.delete(ev.user_id); return n; });
      if (cur && cur.user?.user_id === ev.user_id) setPresence({ online: ev.online, last_seen: ev.last_seen });
      return;
    }
    if (ev.t === "message") {
      const m = ev.message;
      if (cur && m.conversation_id === cur.id) {
        setMessages((prev) => mergeMsg(prev, m));
        if (!m.mine) { if (!atBottomRef.current) setNewCount((c) => c + 1); api.markSeen(cur.id).catch(() => {}); }
      }
      loadConvos();
      return;
    }
    if (ev.t === "seen") {
      if (cur && ev.conversation_id === cur.id && ev.by !== myId) setMessages((prev) => prev.map((x) => (x.mine ? { ...x, status: "seen" } : x)));
      return;
    }
    if (ev.t === "reaction") {
      if (cur && ev.conversation_id === cur.id) setMessages((prev) => prev.map((x) => (x.id === ev.message_id ? { ...x, reactions: reactionsFromRaw(ev.reactions_raw, myId) } : x)));
      return;
    }
    if (ev.t === "unsend") {
      if (cur && ev.conversation_id === cur.id) setMessages((prev) => prev.map((x) => (x.id === ev.message_id ? { ...x, unsent: true, text: "", media: null, reactions: [] } : x)));
      loadConvos();
      return;
    }
    if (ev.t === "typing") {
      if (cur && ev.conversation_id === cur.id && ev.user_id !== myId) {
        setOtherTyping(true);
        if (typingTimer.current) clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setOtherTyping(false), 4000);
      }
      return;
    }
    if (ev.t === "retention") {
      if (cur && ev.conversation_id === cur.id) setActive((a) => ({ ...a, retention: ev.retention }));
    }
  }, [loadConvos, myId]);

  const { status: wsStatus, send: wsSend } = useChatSocket(onWsEvent);

  const openConvo = useCallback(async (c) => {
    setActive(c); activeRef.current = c; setOtherTyping(false); setReply(null); setConvSearch(false); setConvQ(""); setConvResults([]); setNewCount(0);
    setLoadingMsgs(true);
    try {
      const r = await api.messages(c.id);
      setMessages(r.messages); setHasMore(r.has_more);
      scrollToBottom(false);
      loadConvos();
    } catch { setMessages([]); setHasMore(false); }
    finally { setLoadingMsgs(false); }
    try { const p = await api.presence(c.user.user_id); setPresence({ online: p.online, last_seen: p.last_seen }); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadConvos]);

  useEffect(() => {
    const cid = params.get("c");
    if (cid && convos && !active) { const c = convos.find((x) => x.id === cid); if (c) openConvo(c); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convos]);

  useEffect(() => {
    if (atBottom) scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  usePoll(async () => {
    if (wsStatus === "open") return;
    await loadConvos();
    const cur = activeRef.current;
    if (cur) { try { const r = await api.messages(cur.id); setMessages(r.messages); setHasMore(r.has_more); } catch {} }
  }, wsStatus === "open" ? 60000 : (active ? 2500 : 6000), [wsStatus, active]);

  usePoll(async () => { if (pane === "friends") await loadFriends(); }, 15000, [pane, loadFriends]);

  const loadOlder = async () => {
    if (!active || !hasMore || loadingOlder || !messages.length) return;
    setLoadingOlder(true);
    const el = scrollRef.current; const prevH = el ? el.scrollHeight : 0;
    try {
      const r = await api.messages(active.id, messages[0].created_at);
      setMessages((prev) => [...r.messages, ...prev]); setHasMore(r.has_more);
      requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - prevH; });
    } catch {} finally { setLoadingOlder(false); }
  };

  const onScroll = () => {
    const el = scrollRef.current; if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setAtBottom(near); if (near) setNewCount(0);
    if (el.scrollTop < 60 && hasMore && !loadingOlder) loadOlder();
  };

  const doSend = async (media) => {
    if ((!text.trim() && !media) || !active) return;
    const body = media ? "" : text.trim();
    const cid = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const replyTo = reply;
    const optimistic = { id: cid, client_id: cid, conversation_id: active.id, sender_id: myId, mine: true, text: body, media: media || null, reactions: [], reply_to: replyTo ? { id: replyTo.id, sender_id: replyTo.sender_id, text: replyTo.text, media_type: replyTo.media?.type } : null, created_at: new Date().toISOString(), status: "sending", unsent: false };
    setMessages((prev) => [...prev, optimistic]);
    if (!media) setText("");
    setReply(null); scrollToBottom();
    try {
      const m = await api.sendMessage(active.id, body, media || null, replyTo?.id, cid);
      setMessages((prev) => mergeMsg(prev, m));
      loadConvos();
    } catch (e) {
      setMessages((prev) => prev.map((x) => (x.id === cid ? { ...x, status: "failed" } : x)));
      toast(e.message || "Message failed");
    }
  };

  const retry = async (msg) => {
    setMessages((prev) => prev.map((x) => (x.id === msg.id ? { ...x, status: "sending" } : x)));
    try { const m = await api.sendMessage(active.id, msg.text, msg.media || null, msg.reply_to?.id, msg.id); setMessages((prev) => mergeMsg(prev, m)); loadConvos(); }
    catch (e) { setMessages((prev) => prev.map((x) => (x.id === msg.id ? { ...x, status: "failed" } : x))); toast(e.message || "Still failing"); }
  };

  const pickImage = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setUploading(true);
    try { const r = await api.uploadMedia(f); await doSend({ url: r.url, type: r.type }); }
    catch (err) { toast("Upload failed", { description: err.message }); }
    finally { setUploading(false); }
  };

  const react = async (msg, emoji) => {
    setActionMsg(null);
    try { const r = await api.reactMessage(active.id, msg.id, emoji); setMessages((prev) => prev.map((x) => (x.id === msg.id ? { ...x, reactions: r.reactions } : x))); }
    catch (e) { toast(e.message); }
  };
  const removeMsg = async (msg, scope) => {
    setActionMsg(null);
    try {
      await api.deleteMessage(active.id, msg.id, scope);
      if (scope === "me") setMessages((prev) => prev.filter((x) => x.id !== msg.id));
      else setMessages((prev) => prev.map((x) => (x.id === msg.id ? { ...x, unsent: true, text: "", media: null, reactions: [] } : x)));
      loadConvos();
    } catch (e) { toast(e.message); }
  };

  const setRetention = async (mode) => {
    try { await api.setRetention(active.id, mode); setActive((a) => ({ ...a, retention: mode })); setHeadMenu(false); toast(mode === "24h" ? "Messages will disappear after 24 hours" : "Messages kept forever"); }
    catch (e) { toast(e.message); }
  };
  const leaveConvo = async () => {
    setHeadMenu(false);
    try { await api.leaveConversation(active.id); setActive(null); setMessages([]); loadConvos(); toast("Conversation deleted"); }
    catch (e) { toast(e.message); }
  };

  const onInput = (e) => {
    setText(e.target.value);
    const now = Date.now();
    if (active && now - lastTypingSent.current > 2500) {
      lastTypingSent.current = now;
      if (!wsSend({ t: "typing", conversation_id: active.id })) api.setTyping(active.id).catch(() => {});
    }
  };

  const openFriendStory = async (f) => {
    try { const g = await api.userStories(f.username); if (g.stories?.length) setViewer({ groups: [g], index: 0 }); else navigate(`/u/${f.username}`); }
    catch { navigate(`/u/${f.username}`); }
  };

  useEffect(() => {
    if (!showNew || q.trim().length < 1) { setResults([]); return; }
    let on = true;
    const t = setTimeout(async () => { try { const r = await api.searchUsers(q.trim()); if (on) setResults(r); } catch { if (on) setResults([]); } }, 300);
    return () => { on = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, showNew]);

  useEffect(() => {
    if (!convSearch || convQ.trim().length < 2 || !active) { setConvResults([]); return; }
    let on = true;
    const t = setTimeout(async () => { try { const r = await api.searchConversation(active.id, convQ.trim()); if (on) setConvResults(r.results); } catch { if (on) setConvResults([]); } }, 300);
    return () => { on = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convQ, convSearch, active]);

  const startWith = async (username) => {
    try { const c = await api.startConversation(username); setShowNew(false); setQ(""); setResults([]); await loadConvos(); await openConvo(c); }
    catch (e) { toast(e.message); }
  };

  const friendsWithStories = (friends || []).filter((f) => f.has_story);
  const filteredConvos = (convos || []).filter((c) => { const t = listQ.trim().toLowerCase(); return !t || c.user?.name?.toLowerCase().includes(t) || c.user?.username?.toLowerCase().includes(t); });

  const jumpTo = (mid) => {
    const node = document.querySelector(`[data-mid="${mid}"]`);
    if (node) { node.scrollIntoView({ behavior: "smooth", block: "center" }); node.classList.add("ring-2", "ring-aura-cyan"); setTimeout(() => node.classList.remove("ring-2", "ring-aura-cyan"), 1600); setConvSearch(false); }
    else toast("Message is further back in history");
  };

  return (
    <div className="mx-auto flex h-[calc(100dvh-118px-env(safe-area-inset-bottom))] max-w-5xl md:h-screen">
      <aside className={cn("flex w-full flex-col border-r border-am-border md:w-80", active && "hidden md:flex")}>
        <div className="flex items-center justify-between border-b border-am-border px-4 py-4">
          <h1 className="font-display text-xl font-bold text-white">Messages</h1>
          <div className="flex items-center gap-2">
            {wsStatus !== "open" && <span title="Reconnecting…" data-testid="ws-offline"><WifiOff className="h-4 w-4 text-am-text3" /></span>}
            <button onClick={() => setShowNew(true)} data-testid="new-message" className="grid h-9 w-9 place-items-center rounded-full aura-gradient text-black"><MailPlus className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="flex border-b border-am-border">
          {["chats", "friends"].map((p) => (
            <button key={p} onClick={() => setPane(p)} data-testid={`msg-tab-${p}`} className={cn("relative flex-1 py-2.5 text-sm font-semibold capitalize", pane === p ? "text-white" : "text-am-text2")}>
              {p}{pane === p && <span className="absolute bottom-0 left-1/2 h-[3px] w-8 -translate-x-1/2 rounded-full aura-gradient" />}
            </button>
          ))}
        </div>
        {pane === "chats" && (
          <div className="border-b border-am-border px-3 py-2">
            <div className="flex items-center gap-2 rounded-full border border-am-border bg-am-surface2 px-3">
              <Search className="h-4 w-4 text-am-text3" />
              <input value={listQ} onChange={(e) => setListQ(e.target.value)} data-testid="conv-list-search" placeholder="Search chats" className="w-full bg-transparent py-2 text-sm text-white outline-none" />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {pane === "chats" ? (
            convos === null ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-am-text3" /></div>
            : filteredConvos.length === 0 ? <div className="px-6 py-16 text-center"><p className="text-sm font-semibold text-white">{listQ ? "No matches." : "No messages yet."}</p><p className="mt-1 text-xs text-am-text2">{listQ ? "Try another name." : "Start a conversation from Friends or a profile."}</p></div>
            : filteredConvos.map((c) => (
              <button key={c.id} onClick={() => openConvo(c)} data-testid={`conv-${c.user?.username}`} className={cn("flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/5", active?.id === c.id && "bg-white/5")}>
                <div className="relative">
                  <AuraRing src={mediaUrl(c.user?.picture)} size={44} ring={2} fallback={c.user?.name} />
                  {(c.online || online.has(c.user?.user_id)) && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-am-bg bg-aura-mint" data-testid="online-dot" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 truncate text-sm font-semibold text-white">{c.user?.name}{c.user?.is_verified && <VerifiedBadge size={12} />}</span>
                    {c.last?.created_at && <span className="text-[10px] text-am-text3">{timeAgo(c.last.created_at)}</span>}
                  </div>
                  <p className={cn("truncate text-xs", c.unread > 0 ? "font-semibold text-white" : "text-am-text2")}>{c.last ? (c.last.mine ? "You: " : "") + c.last.text : `@${c.user?.username}`}</p>
                </div>
                {c.unread > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full aura-gradient px-1 text-[10px] font-bold text-black" data-testid="unread-badge">{c.unread}</span>}
              </button>
            ))
          ) : (
            friends === null ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-am-text3" /></div>
            : friends.length === 0 ? <div className="px-6 py-16 text-center"><p className="text-sm font-semibold text-white">No friends yet.</p><p className="mt-1 text-xs text-am-text2">When you and someone follow each other, you become friends.</p></div>
            : (
              <>
                {friendsWithStories.length > 0 && (
                  <div className="flex gap-3 overflow-x-auto border-b border-am-border px-4 py-3 no-scrollbar" data-testid="friends-stories">
                    {friendsWithStories.map((f) => (
                      <button key={f.user_id} onClick={() => openFriendStory(f)} className="flex shrink-0 flex-col items-center gap-1">
                        <AuraRing src={mediaUrl(f.picture) || undefined} size={58} ring={2.5} fallback={f.name} story storySeen={f.all_seen} />
                        <span className="max-w-[60px] truncate text-[11px] text-am-text2">{f.name?.split(" ")[0]}</span>
                      </button>
                    ))}
                  </div>
                )}
                {friends.map((f) => (
                  <div key={f.user_id} data-testid={`friend-${f.username}`} className="flex items-center gap-3 px-4 py-3 hover:bg-white/5">
                    <button onClick={() => navigate(`/u/${f.username}`)} className="relative"><AuraRing src={mediaUrl(f.picture)} size={44} ring={2} fallback={f.name} story={f.has_story} storySeen={f.all_seen} />{online.has(f.user_id) && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-am-bg bg-aura-mint" />}</button>
                    <button onClick={() => navigate(`/u/${f.username}`)} className="min-w-0 flex-1 text-left">
                      <div className="flex items-center gap-1 text-sm font-semibold text-white">{f.name}{f.is_verified && <VerifiedBadge size={12} />}</div>
                      <div className="truncate text-xs text-am-text2">@{f.username}{f.show_category !== false && f.category ? ` · ${f.category}` : ""}</div>
                    </button>
                    <button onClick={() => startWith(f.username)} data-testid={`friend-message-${f.username}`} className="shrink-0 rounded-full border border-am-border-strong px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5">Message</button>
                  </div>
                ))}
              </>
            )
          )}
        </div>
      </aside>

      <section className={cn("relative flex flex-1 flex-col", !active && "hidden md:flex")}>
        {active ? (
          <>
            <div className="flex items-center gap-3 border-b border-am-border px-4 py-3">
              <button onClick={() => { setActive(null); activeRef.current = null; }} className="md:hidden" data-testid="back-to-list"><ArrowLeft className="h-5 w-5 text-am-text2" /></button>
              <button onClick={() => navigate(`/u/${active.user?.username}`)} data-testid="chat-profile-link" className="flex items-center gap-3">
                <div className="relative"><AuraRing src={mediaUrl(active.user?.picture)} size={38} ring={2} fallback={active.user?.name} />{(presence?.online || online.has(active.user?.user_id)) && <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-am-bg bg-aura-mint" />}</div>
                <div className="text-left"><div className="flex items-center gap-1 text-sm font-semibold text-white">{active.user?.name}{active.user?.is_verified && <VerifiedBadge size={13} />}</div><div className="text-xs text-am-text2" data-testid="presence-text">{otherTyping ? "typing…" : presenceText(presence)}</div></div>
              </button>
              <div className="ml-auto flex items-center gap-1">
                <button onClick={() => { setConvSearch((s) => !s); setConvQ(""); setConvResults([]); }} data-testid="conv-search-toggle" className="grid h-9 w-9 place-items-center rounded-full text-am-text2 hover:bg-white/5"><Search className="h-5 w-5" /></button>
                <div className="relative">
                  <button onClick={() => setHeadMenu((m) => !m)} data-testid="retention-menu" className="grid h-9 w-9 place-items-center rounded-full text-am-text2 hover:bg-white/5"><MoreVertical className="h-5 w-5" /></button>
                  {headMenu && (
                    <div className="absolute right-0 top-10 z-30 w-56 overflow-hidden rounded-xl border border-am-border bg-am-surface2 p-1 shadow-xl">
                      <div className="px-3 py-1.5 text-[11px] font-semibold uppercase text-am-text3">Message history</div>
                      <button onClick={() => setRetention("forever")} data-testid="retention-forever" className={cn("flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-white/5", (active.retention || "forever") === "forever" ? "text-white" : "text-am-text2")}>Keep forever {(active.retention || "forever") === "forever" && <span className="h-2 w-2 rounded-full aura-radial" />}</button>
                      <button onClick={() => setRetention("24h")} data-testid="retention-24h" className={cn("flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-white/5", active.retention === "24h" ? "text-white" : "text-am-text2")}>Delete after 24 hours {active.retention === "24h" && <span className="h-2 w-2 rounded-full aura-radial" />}</button>
                      <div className="my-1 h-px bg-am-border" />
                      <button onClick={leaveConvo} data-testid="leave-conversation" className="flex w-full items-center gap-2 px-3 py-2 text-sm text-aura-magenta hover:bg-white/5"><Trash2 className="h-4 w-4" /> Delete conversation</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {convSearch && (
              <div className="border-b border-am-border px-4 py-2" data-testid="conv-search-bar">
                <div className="flex items-center gap-2 rounded-full border border-am-border bg-am-surface2 px-3">
                  <Search className="h-4 w-4 text-am-text3" />
                  <input autoFocus value={convQ} onChange={(e) => setConvQ(e.target.value)} data-testid="conv-search-input" placeholder="Search in conversation" className="w-full bg-transparent py-2 text-sm text-white outline-none" />
                </div>
                {convResults.length > 0 && (
                  <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                    {convResults.map((r) => (
                      <button key={r.id} onClick={() => jumpTo(r.id)} className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-white/5">
                        <span className="truncate text-sm text-am-text">{r.text}</span>
                        <span className="shrink-0 text-[10px] text-am-text3">{timeAgo(r.created_at)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {active.retention === "24h" && <div className="flex items-center justify-center gap-1.5 bg-am-surface/60 py-1.5 text-[11px] text-am-text2"><Clock className="h-3 w-3" /> Messages disappear after 24 hours</div>}

            <div ref={scrollRef} onScroll={onScroll} className="flex-1 space-y-1.5 overflow-y-auto p-4">
              {loadingOlder && <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-am-text3" /></div>}
              {loadingMsgs ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-am-text3" /></div>
                : messages.length === 0 ? <div className="flex h-full flex-col items-center justify-center text-center"><p className="text-sm font-semibold text-white">Say hello 👋</p><p className="mt-1 text-xs text-am-text2">This is the start of your conversation with {active.user?.name}.</p></div>
                : messages.map((m) => {
                  const mine = m.mine ?? (m.sender_id === myId);
                  const color = userColor(m.sender_id || (mine ? myId : active.user?.user_id));
                  return (
                    <div key={m.id} data-mid={m.id} className={cn("flex rounded-lg", mine ? "justify-end" : "justify-start")} data-testid={mine ? "msg-mine" : "msg-theirs"}>
                      <div className="max-w-[78%]">
                        {m.reply_to && (
                          <div className={cn("mb-0.5 truncate rounded-lg border-l-2 bg-am-surface2/70 px-2 py-1 text-[11px] text-am-text2", mine ? "ml-auto" : "")} style={{ borderColor: color }}>
                            {m.reply_to.media_type ? `↩︎ ${m.reply_to.media_type}` : `↩︎ ${m.reply_to.text || "message"}`}
                          </div>
                        )}
                        <div onClick={() => !m.unsent && setActionMsg(m)}
                          className={cn("relative cursor-pointer overflow-hidden rounded-2xl border bg-am-surface2 text-sm transition-shadow", m.media ? "p-1" : "px-3.5 py-2", m.unsent && "cursor-default")}
                          style={{ borderColor: color + "55", boxShadow: `inset 2px 0 0 ${mine ? "transparent" : color}, inset -2px 0 0 ${mine ? color : "transparent"}` }}>
                          {m.unsent ? <span className="italic text-am-text3">Message deleted</span>
                            : m.media ? (
                              m.media.type === "video"
                                ? <video src={mediaUrl(m.media.url)} controls className="max-h-72 rounded-xl" />
                                : <img src={mediaUrl(m.media.url)} alt="" className="max-h-72 rounded-xl" />
                            ) : <span style={{ color }} className="whitespace-pre-wrap font-medium"><Linkify text={m.text} /></span>}
                        </div>
                        {m.reactions?.length > 0 && (
                          <div className={cn("mt-0.5 flex flex-wrap gap-1", mine ? "justify-end" : "")}>
                            {m.reactions.map((r) => (
                              <button key={r.emoji} onClick={() => react(m, r.emoji)} className={cn("flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[11px]", r.me ? "border-aura-cyan/50 bg-aura-cyan/10" : "border-am-border bg-am-surface2")} data-testid="msg-reaction">
                                <span>{r.emoji}</span>{r.count > 1 && <span className="text-am-text2">{r.count}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className={cn("mt-0.5 flex items-center gap-1 text-[10px] text-am-text3", mine ? "justify-end" : "")}>
                          <span>{timeAgo(m.created_at)}</span>
                          {mine && !m.unsent && <StatusTicks status={m.status} />}
                          {mine && m.status === "failed" && <button onClick={() => retry(m)} data-testid="retry-send" className="ml-1 flex items-center gap-0.5 font-semibold text-aura-magenta"><RotateCcw className="h-3 w-3" /> Retry</button>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              {otherTyping && (
                <div className="flex justify-start" data-testid="typing-indicator">
                  <div className="flex items-center gap-1 rounded-2xl border border-am-border bg-am-surface2 px-4 py-3" style={{ borderColor: userColor(active.user?.user_id) + "55" }}>
                    {[0, 150, 300].map((d) => <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-am-text2" style={{ animationDelay: `${d}ms` }} />)}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {newCount > 0 && !atBottom && (
              <button onClick={() => { scrollToBottom(); setNewCount(0); }} data-testid="new-messages-pill" className="absolute bottom-20 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full aura-gradient px-3 py-1.5 text-xs font-bold text-black shadow-lg">
                <ChevronDown className="h-4 w-4" /> {newCount} new message{newCount > 1 ? "s" : ""}
              </button>
            )}

            {reply && (
              <div className="flex items-center justify-between gap-2 border-t border-am-border bg-am-surface/60 px-4 py-2" data-testid="reply-bar">
                <div className="min-w-0"><div className="text-[11px] font-semibold text-aura-cyan">Replying to {reply.mine ? "yourself" : active.user?.name}</div><div className="truncate text-xs text-am-text2">{reply.media ? reply.media.type : reply.text}</div></div>
                <button onClick={() => setReply(null)} className="text-am-text2 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
            )}
            <div className="flex items-center gap-2 border-t border-am-border p-3">
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="message-image" className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-am-border text-am-text2 hover:text-white disabled:opacity-50">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-5 w-5" />}</button>
              <input ref={fileRef} type="file" hidden accept="image/*,video/*" onChange={pickImage} />
              <input value={text} onChange={onInput} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); doSend(); } }} data-testid="message-input" placeholder="Message…" className="flex-1 rounded-full border border-am-border bg-am-surface2 px-4 py-2.5 text-sm text-white placeholder:text-am-text3 outline-none focus:border-am-border-strong" />
              <button onClick={() => doSend()} data-testid="message-send" className="grid h-10 w-10 shrink-0 place-items-center rounded-full aura-gradient text-black active:scale-90"><Send className="h-4 w-4" strokeWidth={2.4} /></button>
            </div>
          </>
        ) : (
          <div className="hidden flex-1 items-center justify-center md:flex"><p className="text-sm text-am-text2">Select a conversation.</p></div>
        )}
      </section>

      {actionMsg && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 sm:items-center" onClick={() => setActionMsg(null)} data-testid="msg-action-sheet">
          <div className="w-full max-w-md rounded-t-2xl border border-am-border bg-am-surface p-3 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex justify-center gap-1.5">
              {QUICK.map((e) => <button key={e} onClick={() => react(actionMsg, e)} data-testid={`react-${e}`} className="grid h-11 w-11 place-items-center rounded-full text-2xl transition-transform hover:scale-110 active:scale-95">{e}</button>)}
            </div>
            <div className="h-px bg-am-border" />
            <button onClick={() => { setReply(actionMsg); setActionMsg(null); }} data-testid="action-reply" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-white hover:bg-white/5"><Reply className="h-4 w-4" /> Reply</button>
            {!actionMsg.media && <button onClick={() => { navigator.clipboard?.writeText(actionMsg.text).catch(() => {}); setActionMsg(null); toast("Copied"); }} data-testid="action-copy" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-white hover:bg-white/5"><Copy className="h-4 w-4" /> Copy</button>}
            <button onClick={() => removeMsg(actionMsg, "me")} data-testid="action-delete-me" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-white hover:bg-white/5"><Trash2 className="h-4 w-4" /> Delete for me</button>
            {actionMsg.mine && <button onClick={() => removeMsg(actionMsg, "everyone")} data-testid="action-unsend" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-aura-magenta hover:bg-white/5"><Ban className="h-4 w-4" /> Unsend for everyone</button>}
          </div>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/70 p-4 pt-20" onClick={() => setShowNew(false)}>
          <div className="w-full max-w-md rounded-2xl border border-am-border bg-am-surface p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between"><h3 className="font-semibold text-white">New message</h3><button onClick={() => setShowNew(false)}><X className="h-4 w-4 text-am-text2" /></button></div>
            <div className="flex items-center gap-2 rounded-full border border-am-border bg-am-surface2 px-3">
              <Search className="h-4 w-4 text-am-text3" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="new-message-search" placeholder="Search people…" className="w-full bg-transparent py-2.5 text-sm text-white outline-none" />
            </div>
            <div className="mt-2 max-h-72 overflow-y-auto">
              {results.map((u) => (
                <button key={u.user_id} onClick={() => startWith(u.username)} className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-white/5">
                  <AuraRing src={mediaUrl(u.picture)} size={40} ring={2} fallback={u.name} />
                  <div><div className="flex items-center gap-1 text-sm font-semibold text-white">{u.name}{u.is_verified && <VerifiedBadge size={12} />}</div><div className="text-xs text-am-text2">@{u.username}</div></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {viewer && <StoryViewer groups={viewer.groups} startIndex={viewer.index} onClose={() => setViewer(null)} onViewed={loadFriends} />}
    </div>
  );
}
